import React, {useEffect} from 'react'
import {RemotePart} from '@dv5/api'
import {AppMode, ItemAction, PartType, TagFiltered, useAppSelector, emptyRemoteParts} from '@dv5/domain'
import {clipboardService, markdownService} from '@dv5/components'
import {DocumentPartActions} from './panels/DocumentPartActions'
import {DocumentParamsEditor} from './components/DocumentParamsEditor'
import {PartHeaderInfo} from './components/PartHeaderInfo'
import {remoteDocumentPartsService} from './services/remoteDocumentPartsService'
import {documentApi} from './services/documentService'
import './DocumentView.less'
import './panels/DocumentPartView.less'
import {useRemotePartEditor} from './components/useRemotePartEditor'
import {DocumentPartEditor} from './components/DocumentPartEditor'
import {DocumentPartViewer} from './components/DocumentPartViewer'

interface RemotePartViewProps {
    documentId: string
    part: RemotePart
    allTags: TagFiltered[]
}

export const RemotePartView = ({documentId, part, allTags}: RemotePartViewProps) => {
    const {
        draft,
        saving,
        error,
        save,
        cancel,
        changeDraft,
        toggleEdit,
        manualSave,
        retrySave,
        saveStatus,
        lastSavedTime
    }
        = useRemotePartEditor(documentId, part)
    const copy = (action?: string) => {
        if (action === 'export-csv') {
            documentApi.exportToCsv(part);
            return
        }
        let content = part.partContent
        if ((action === 'copy' || action === 'copy-without-header') && part.partType === PartType.MARKDOWN) {
            content = markdownService.getAsPlainText(markdownService.parseMarkdown(content))
        }
        if (action === 'copy' && part.partHeader) content = part.partHeader + '\n\n' + content
        clipboardService.copyTo(content, true)
    }
    const hasContent = !!draft?.partContent.trim()
    const allowedTypes = !hasContent ? undefined
        : draft && [PartType.MARKDOWN, PartType.PLAINTEXT].includes(draft.partType)
            ? [PartType.MARKDOWN, PartType.PLAINTEXT] : draft ? [draft.partType] : undefined
    return <div className="documentPart" aria-busy={saving}>
        <div inert={saving && !draft}>
            <DocumentPartActions editMode={!!draft} mode={AppMode.VIEW} part={part} documentId={documentId}
                                 arrangeProps={{mode: AppMode.VIEW, index: 0, groupCount: -1, count: 1}}
                                 onSwap={() => {
                                 }} onMenuAction={action => {
                if (action === ItemAction.EDIT) toggleEdit()
            }}
                                 viewMenuItems={[{text: 'Start edit', action: ItemAction.EDIT}]}
                                 editMenuItems={[{text: 'Stop edit', action: ItemAction.EDIT}]}
                                 showComments={false} showArrange={false}/>
        </div>
        <div className="documentPartContent">
            {!draft && <>
                <PartHeaderInfo header={part.partHeader} type={part.partType} onCopy={copy}/>
                <div inert={saving}><DocumentPartViewer part={part}
                                                        onChange={(_, content) => void save({
                                                            ...part,
                                                            partContent: content
                                                        })}/></div>
            </>}
            {draft && <div>
                <DocumentParamsEditor part={draft} documentId={documentId}
                                      tags={(draft.tags || '').split(',').filter(Boolean)} allTags={allTags}
                                      onHeaderInput={header => changeDraft({partHeader: header})}
                                      onHeaderChange={header => changeDraft({partHeader: header})}
                                      onTypeChange={type => changeDraft({partType: type})}
                                      onTagsChange={tags => changeDraft({tags})}
                                      onCancel={cancel} allowedTypes={allowedTypes}/>
                <DocumentPartEditor part={draft} partType={draft.partType} content={draft.partContent}
                                    setContent={content => changeDraft({partContent: content})}
                                    saveStatus={saveStatus} lastSavedTime={lastSavedTime}
                                    handleManualSave={manualSave} cancelEdit={cancel}/>
            </div>}
            {draft && part.revision !== draft.revision &&
                <p role="status">This part changed remotely. Your draft is retained. Cancel to load the latest
                    version.</p>}
            {saving && <p role="status">Saving on agent…</p>}
            {error && <p role="alert">{error}{' '}
                <button type="button" disabled={saving}
                        onClick={() => remoteDocumentPartsService.reload(documentId)}>Reload latest parts
                </button>
                {' '}
                <button type="button" disabled={saving} onClick={retrySave}>Retry save</button>
            </p>}
        </div>
    </div>
}

export const RemoteDocumentParts = ({documentId, allTags = []}: { documentId: string; allTags?: TagFiltered[] }) => {
    const {parts, error, loaded, loading} = useAppSelector(state => state.remoteParts[documentId] || emptyRemoteParts)
    useEffect(() => remoteDocumentPartsService.subscribe(documentId), [documentId])
    return <div className="documentParts">
        {!loaded && !error && <p role="status">Loading parts from agent…</p>}
        {error && <p role="alert">{error}
            <button type="button" disabled={loading}
                    onClick={() => remoteDocumentPartsService.reload(documentId)}>Retry
            </button>
        </p>}
        {loaded && parts.length === 0 && <p>No parts yet. Use Add part to create one.</p>}
        {parts.map(part => <RemotePartView key={documentId + part.id} documentId={documentId} part={part}
                                           allTags={allTags}/>)}
    </div>
}
